/**
 * useChat — POST /chat + consume SSE.
 *
 * Owns: outgoing message POST, SSE event parsing, accumulator state for
 * (text deltas + tool chips + error). The chat UI is a pure presentation
 * of useChat's `messages` array.
 *
 * AG-UI events consumed (per spec §6.1):
 *   - RUN_STARTED              → noop (turn begins; isStreaming already true from send())
 *   - TEXT_MESSAGE_START       → start a new assistant message bubble
 *   - TEXT_MESSAGE_CONTENT     → append delta to current assistant message
 *   - TEXT_MESSAGE_END         → mark assistant message status=done
 *   - TOOL_CALL_START          → push a running toolCall chip on the active message
 *   - TOOL_CALL_END            → mark chip done with durationMs
 *   - CUSTOM widget.directive  → ignored (K.4 emit-widget-directive writes inline
 *                                directive text via TEXT_MESSAGE_CONTENT instead)
 *   - RUN_FINISHED             → mark turn complete; isStreaming=false
 *   - RUN_ERROR                → mark error; isStreaming=false
 *
 * The pure functions parseSSEEvents + applyEvent are exported for unit
 * tests; the hook is a thin React wrapper.
 */

import { useCallback, useState, useRef } from 'react';

export type ChatMessage =
  | { id: string; role: 'user'; content: string }
  | { id: string; role: 'assistant'; content: string; toolCalls?: ToolCallChip[]; status: 'streaming' | 'done' | 'error' };

export type ToolCallChip = {
  id: string;
  toolName: string;
  summary: string;
  durationMs?: number;
  status: 'running' | 'done' | 'error';
};

export type ChatState = {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
};

export type SSEEvent = { event: string; data: string };

export type SendOptions = {
  /** Inject for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
};

export type UseChatResult = {
  messages: ReadonlyArray<ChatMessage>;
  send: (content: string, opts?: SendOptions) => Promise<void>;
  /** K.5 — append a user prompt + synthetic assistant cell render in one step.
   *  Used by the chip-click fast path (spec §3.4) which bypasses the LLM. */
  appendCell: (userLabel: string, cellMarkdown: string) => void;
  /**
   * POST `url` with `body`, then consume the AG-UI SSE response and append
   * the resulting assistant message(s) to `messages`. Does NOT push a user
   * message first (the caller's click is not user text).
   * Throws when the pre-stream POST fails (!res.ok || !res.body) — the caller
   * (admin decision widget) needs to know the audit event did not land.
   */
  resumeFrom: (url: string, body: Record<string, unknown>, opts?: SendOptions) => Promise<void>;
  isStreaming: boolean;
  error: string | null;
  convId: string;
};

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Pure SSE parser ─────────────────────────────────────────────────

/**
 * Pure SSE parser. Splits a buffer on `\n\n`; returns parsed events +
 * bytes consumed. Incomplete trailing blocks are left in the buffer by
 * the caller (consumed < buffer.length).
 */
export function parseSSEEvents(buffer: string): { events: SSEEvent[]; consumed: number } {
  const events: SSEEvent[] = [];
  let consumed = 0;
  let idx = 0;

  while (idx < buffer.length) {
    const blank = buffer.indexOf('\n\n', idx);
    if (blank === -1) break;
    const block = buffer.slice(idx, blank);
    let event = 'message';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) event = line.slice(7).trim();
      else if (line.startsWith('data: ')) data += line.slice(6);
    }
    events.push({ event, data });
    consumed = blank + 2;
    idx = consumed;
  }

  return { events, consumed };
}

// ─── Pure reducer ────────────────────────────────────────────────────

/**
 * Pure reducer. Given a state + one SSE event, return the next state.
 * Always returns a new object (no mutation).
 */
export function applyEvent(state: ChatState, evt: SSEEvent): ChatState {
  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(evt.data) as Record<string, unknown>;
  } catch {
    // tolerate malformed JSON — treat as empty payload
  }

  switch (evt.event) {
    case 'TEXT_MESSAGE_START': {
      const messageId = String(payload.messageId ?? genId('msg'));
      // Idempotent: skip if already exists.
      if (state.messages.some((m) => m.id === messageId)) return state;
      const msg: ChatMessage = {
        id: messageId,
        role: 'assistant',
        content: '',
        status: 'streaming',
      };
      return { ...state, messages: [...state.messages, msg] };
    }

    case 'TEXT_MESSAGE_CONTENT': {
      const messageId = String(payload.messageId ?? '');
      const delta = String(payload.delta ?? '');
      const messages = state.messages.map((m) => {
        if (m.id !== messageId || m.role !== 'assistant') return m;
        return { ...m, content: m.content + delta };
      });
      return { ...state, messages };
    }

    case 'TEXT_MESSAGE_END': {
      const messageId = String(payload.messageId ?? '');
      const messages = state.messages.map((m) => {
        if (m.id !== messageId || m.role !== 'assistant') return m;
        return { ...m, status: 'done' as const };
      });
      return { ...state, messages };
    }

    case 'TOOL_CALL_START': {
      const chip: ToolCallChip = {
        id: String(payload.toolCallId ?? genId('tc')),
        toolName: String(payload.toolName ?? 'tool'),
        summary: String(payload.summary ?? ''),
        status: 'running',
      };
      // Attach to the most recent assistant message.
      const lastIdx = [...state.messages].reverse().findIndex((m) => m.role === 'assistant');
      if (lastIdx === -1) return state; // No active assistant message; ignore.
      const trueIdx = state.messages.length - 1 - lastIdx;
      const messages = state.messages.map((m, i) => {
        if (i !== trueIdx || m.role !== 'assistant') return m;
        return { ...m, toolCalls: [...(m.toolCalls ?? []), chip] };
      });
      return { ...state, messages };
    }

    case 'TOOL_CALL_END': {
      const id = String(payload.toolCallId ?? '');
      const durationMs = typeof payload.durationMs === 'number' ? payload.durationMs : undefined;
      const messages = state.messages.map((m) => {
        if (m.role !== 'assistant' || !m.toolCalls) return m;
        return {
          ...m,
          toolCalls: m.toolCalls.map((c) =>
            c.id === id ? { ...c, status: 'done' as const, durationMs } : c,
          ),
        };
      });
      return { ...state, messages };
    }

    case 'RUN_FINISHED':
      return { ...state, isStreaming: false };

    case 'RUN_ERROR':
      return {
        ...state,
        isStreaming: false,
        error: String(payload.message ?? 'unknown error'),
        // CF#5: close any still-streaming assistant bubble so the UI doesn't
        // hang on a perpetual "streaming" state after a mid-stream error.
        messages: state.messages.map((m) =>
          m.role === 'assistant' && m.status === 'streaming' ? { ...m, status: 'error' as const } : m,
        ),
      };

    default:
      // RUN_STARTED and CUSTOM events are intentionally ignored.
      return state;
  }
}

// ─── React hook ──────────────────────────────────────────────────────

export function useChat(opts: { convId?: string; api?: string; body?: Record<string, unknown> } = {}): UseChatResult {
  const [state, setState] = useState<ChatState>({ messages: [], isStreaming: false, error: null });
  // convId is intentionally stable: initialized once, never updated (unlike
  // apiRef/bodyRef below) — a conversation id must be constant for the session.
  const convIdRef = useRef(opts.convId ?? genId('conv'));
  const isStreamingRef = useRef(false);
  // Refs so send/resumeFrom don't need to re-create when api/body change.
  const apiRef = useRef(opts.api ?? '/chat');
  const bodyRef = useRef(opts.body ?? {});
  // Keep refs current on re-renders.
  apiRef.current = opts.api ?? '/chat';
  bodyRef.current = opts.body ?? {};

  /**
   * Shared helper — drain an AG-UI SSE response body into state.
   * Both send() and resumeFrom() delegate here after making their POST.
   * useCallback([]) so the stable send/resumeFrom closures capture one
   * instance; it only closes over the stable setState + isStreamingRef.
   */
  const consumeStream = useCallback(async (res: Response): Promise<void> => {
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, consumed } = parseSSEEvents(buffer);
        if (consumed > 0) buffer = buffer.slice(consumed);
        for (const evt of events) {
          setState((s) => applyEvent(s, evt));
        }
      }
    } catch (err) {
      isStreamingRef.current = false;
      setState((s) => ({ ...s, error: `stream: ${(err as Error).message}`, isStreaming: false }));
      return;
    } finally {
      // Release the lock on res.body so a fully-consumed reader doesn't leave
      // the stream locked (matters for test mocks; no-op on a drained reader).
      reader.releaseLock();
    }

    // Stream closed normally; if neither RUN_FINISHED nor RUN_ERROR cleared
    // isStreaming, force-clear it so the UI re-enables the input.
    isStreamingRef.current = false;
    setState((s) => (s.isStreaming ? { ...s, isStreaming: false } : s));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback(async (content: string, sendOpts?: SendOptions) => {
    if (isStreamingRef.current) return;

    isStreamingRef.current = true;
    const userMsg: ChatMessage = { id: genId('msg'), role: 'user', content };
    setState((s) => ({ ...s, messages: [...s.messages, userMsg], isStreaming: true, error: null }));

    const turnId = genId('turn');
    const runId = genId('run');
    const fetchImpl = sendOpts?.fetchImpl ?? fetch;

    let res: Response;
    try {
      res = await fetchImpl(apiRef.current, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          content,
          convId: convIdRef.current,
          turnId,
          runId,
          ...bodyRef.current,
        }),
      });
    } catch (err) {
      isStreamingRef.current = false;
      setState((s) => ({ ...s, error: `network: ${(err as Error).message}`, isStreaming: false }));
      return;
    }

    if (!res.ok || !res.body) {
      isStreamingRef.current = false;
      setState((s) => ({ ...s, error: `server returned ${res.status}`, isStreaming: false }));
      return;
    }

    await consumeStream(res);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resumeFrom = useCallback(async (url: string, body: Record<string, unknown>, sendOpts?: SendOptions) => {
    if (isStreamingRef.current) return;

    isStreamingRef.current = true;
    setState((s) => ({ ...s, isStreaming: true, error: null }));

    const fetchImpl = sendOpts?.fetchImpl ?? fetch;

    let res: Response;
    try {
      res = await fetchImpl(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (err) {
      isStreamingRef.current = false;
      setState((s) => ({ ...s, error: `network: ${(err as Error).message}`, isStreaming: false }));
      throw err;
    }

    if (!res.ok || !res.body) {
      isStreamingRef.current = false;
      setState((s) => ({ ...s, error: `server returned ${res.status}`, isStreaming: false }));
      throw new Error(`decision failed: ${res.status}`);
    }

    await consumeStream(res);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const appendCell = useCallback((userLabel: string, cellMarkdown: string) => {
    if (isStreamingRef.current) return;
    const userMsg: ChatMessage = { id: genId('msg'), role: 'user', content: userLabel };
    const assistantMsg: ChatMessage = {
      id: genId('msg'),
      role: 'assistant',
      content: cellMarkdown,
      status: 'done',
    };
    setState((s) => ({
      ...s,
      messages: [...s.messages, userMsg, assistantMsg],
      error: null,
    }));
  }, []);

  return {
    messages: state.messages,
    send,
    appendCell,
    resumeFrom,
    isStreaming: state.isStreaming,
    error: state.error,
    convId: convIdRef.current,
  };
}
